// Copyright (c) Microsoft. All rights reserved.

import { INotification } from '../../../components/AppContainer/ChatView/Notifications/INotification';
import { AuthConfig } from '../../../libs/auth/AuthHelper';
import { AlertDialogs } from '../../../libs/models/AlertDialogs';
import { AlertType } from '../../../libs/models/AlertType';
import { IArchiveSourceAvailability } from '../../../libs/models/ArchiveSourceAvailability';
import { IChatUser } from '../../../libs/models/ChatUser';
import { ServiceInfo } from '../../../libs/models/ServiceInfo';
import { TokenUsage } from '../../../libs/models/TokenUsage';

// This is the default user information when authentication is set to 'None'.
// It must match what is defined in PassthroughAuthenticationHandler.cs on the backend.
export const DefaultChatUser: IChatUser = {
    id: 'c05c61eb-65e4-4223-915a-fe72b0c9ece1',
    emailAddress: 'user@contoso.com',
    fullName: 'Default User',
    online: true,
    isTyping: false,
};

export const DefaultActiveUserInfo: ActiveUserInfo = {
    id: DefaultChatUser.id,
    email: DefaultChatUser.emailAddress,
    username: DefaultChatUser.fullName,
};

export interface ActiveUserInfo {
    id: string;
    email: string;
    username: string;
}

export interface Alert {
    message: string;
    type: AlertType;
    id?: string;
    onRetry?: () => void;
    showDialog?: boolean;
    hideDismissButton?: boolean;
    alertDialog?: AlertDialogs;
}

interface Feature {
    enabled: boolean; // Whether to show the feature in the UX
    label: string;
    inactive?: boolean; // Set to true if you don't want the user to control the visibility of this feature or there's no backend support
    description?: string;
}

export interface Setting {
    title: string;
    description?: string;
    features: FeatureKeys[];
    stackVertically?: boolean;
    learnMoreLink?: string;
}

export enum IConversationDisplayMode {
    Active,
    Hidden,
    All,
}

export enum AppTabs {
    Chat = 'chat',
    Documents = 'documents',
    Sites = 'sites',
}

export interface AppState {
    alerts: Alert[];
    activeUserInfo?: ActiveUserInfo;
    authConfig?: AuthConfig | null;
    tokenUsage: TokenUsage;
    features: Record<FeatureKeys, Feature>;
    settings: Setting[];
    conversationDisplayMode: IConversationDisplayMode;
    archiveSourceAvailability?: IArchiveSourceAvailability;
    serviceInfo: ServiceInfo;
    isMaintenance: boolean;
    appErrorState?: boolean;
    notifications?: INotification[];
    isChatNotificationsEnabled?: boolean;
    isQRCodeEnabled?: boolean;
    isHideChatListOnLoadEnabled?: boolean;
    isAutohideChatListEnabled?: boolean;
    selectedTab: AppTabs;
    signalRConnectionError: string;
}

export enum FeatureKeys {
    DarkMode,
    SimplifiedExperience,
    AzureContentSafety,
    AzureAISearch,
    BotAsDocs,
    MultiUserChat,
    UserFeedback,
    ShowPrompt,
    ShowEffectivePromptsLink,
    HiddenChats,
    DeleteChat,
    ChatGuidance,
    AutoCreateChat,
}

export const Features = {
    [FeatureKeys.DarkMode]: {
        enabled: false,
        label: 'Dark Mode',
    },
    [FeatureKeys.SimplifiedExperience]: {
        enabled: true,
        label: 'Simplified Chat Experience',
    },
    [FeatureKeys.AzureContentSafety]: {
        enabled: false,
        label: 'Azure Content Safety',
        inactive: true,
    },
    [FeatureKeys.AzureAISearch]: {
        enabled: false,
        label: 'Azure AI Search',
        inactive: true,
    },
    [FeatureKeys.BotAsDocs]: {
        enabled: false,
        label: 'Export Chat Sessions',
    },
    [FeatureKeys.MultiUserChat]: {
        enabled: import.meta.env.VITE_FEATURE_REMOVE_MULTI_USER_CHAT?.toLowerCase() !== 'true',
        label: 'Live Chat Session Sharing',
        description: 'Enable multi-user chat sessions. Not available when authorization is disabled.',
    },
    [FeatureKeys.UserFeedback]: {
        enabled: true,
        label: 'Message User Feedback',
        description: 'Enable users to vote on model-generated responses.',
    },
    [FeatureKeys.ShowPrompt]: {
        enabled: true,
        label: 'Show Prompt',
    },
    [FeatureKeys.ShowEffectivePromptsLink]: {
        enabled: true,
        label: 'Show Effective Prompts',
    },
    [FeatureKeys.HiddenChats]: {
        enabled: true,
        label: 'Enable Hidden Chats feature',
    },
    [FeatureKeys.DeleteChat]: {
        enabled: false,
        label: 'Enable Chat Deletion',
        inactive: true,
    },
    [FeatureKeys.ChatGuidance]: {
        enabled: true,
        label: 'Enable Chat Guidance',
    },
    [FeatureKeys.AutoCreateChat]: {
        enabled: import.meta.env.VITE_REMOVE_AUTO_CREATE_CHAT?.toLowerCase() !== 'true',
        label: 'Create new chat for new browser session',
    },
};

export const Settings = [
    {
        // Basic settings has to stay at the first index. Add all new settings to end of array.
        title: 'Basic',
        features: [FeatureKeys.DarkMode],
        stackVertically: true,
    },
    {
        title: 'Display',
        features: [FeatureKeys.SimplifiedExperience],
        stackVertically: true,
    },
];

export const initialState: AppState = {
    alerts: [],
    activeUserInfo: DefaultActiveUserInfo,
    authConfig: {} as AuthConfig,
    tokenUsage: {},
    features: Features,
    settings: Settings,
    conversationDisplayMode: IConversationDisplayMode.Active,
    serviceInfo: {
        memoryStore: { types: [], selectedType: '' },
        availablePlugins: [],
        version: '',
        isContentSafetyEnabled: false,
    },
    isMaintenance: false,
    archiveSourceAvailability: { loaded: false, Sources: [] },
    notifications: [],
    isChatNotificationsEnabled: JSON.parse(localStorage.getItem('chatNotificationsEnabled') || 'true'),
    isQRCodeEnabled: JSON.parse(localStorage.getItem('chatQRCodeEnabled') || 'true'),
    isHideChatListOnLoadEnabled: JSON.parse(localStorage.getItem('hideChatListOnLoadEnabled') || 'false'),
    isAutohideChatListEnabled: JSON.parse(localStorage.getItem('autohideChatListEnabled') || 'false'),
    selectedTab: AppTabs.Chat,
    signalRConnectionError: '',
};
